import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import apiService, { PipelineDigestPreview } from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';

const AUTO_REFRESH_MS = 3000;

const AdminPipelineDigestPreview: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const dateParam = searchParams.get('date');
  const reportDate = dateParam?.trim() || undefined;

  const setReportDate = useCallback(
    (next: string) => {
      setSearchParams(next ? { date: next } : {}, { replace: true });
    },
    [setSearchParams],
  );

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery<PipelineDigestPreview>({
    queryKey: ['pipeline-digest-preview', reportDate ?? 'yesterday-clt'],
    queryFn: () => apiService.getPipelineDigestPreview(reportDate),
    staleTime: 0,
  });

  const dateFieldValue = reportDate ?? data?.reportDateStr ?? '';

  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      void refetch();
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, refetch]);

  const updatedLabel = useMemo(() => {
    if (!dataUpdatedAt) return null;
    return new Date(dataUpdatedAt).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }, [dataUpdatedAt]);

  return (
    <Container maxWidth={false} sx={{ py: 3, maxWidth: 960 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Button
            component={RouterLink}
            to={ROUTES_NAMES.ADMIN_PIPELINES}
            startIcon={<BackIcon />}
            size="small"
            sx={{ mb: 1 }}
          >
            Pipelines
          </Button>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Pipeline digest preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live render of the daily status email. Edit{' '}
            <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
              pipelineDigestEmailTemplate.ts
            </Typography>{' '}
            in app-backend, then refresh — no email sent.
          </Typography>
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
        >
          <TextField
            label="Report date (CLT)"
            type="date"
            size="small"
            value={dateFieldValue}
            onChange={(e) => setReportDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 200 } }}
          />
          <Button
            variant="outlined"
            startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(_, v) => setAutoRefresh(v)}
                size="small"
              />
            }
            label={`Auto-refresh (${AUTO_REFRESH_MS / 1000}s)`}
          />
          {updatedLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
              Updated {updatedLabel}
            </Typography>
          )}
        </Stack>
      </Paper>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load preview. Check that app-backend is running.
        </Alert>
      )}

      {data && (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="HTML email" />
            <Tab label="Plain text" />
            <Tab label="Metrics JSON" />
          </Tabs>

          {tab === 0 && (
            <Paper
              variant="outlined"
              sx={{
                overflow: 'hidden',
                bgcolor: '#f4f4f5',
                minHeight: 720,
              }}
            >
              <Box
                component="iframe"
                title="Pipeline digest email preview"
                srcDoc={data.html}
                sandbox="allow-same-origin"
                sx={{
                  display: 'block',
                  width: '100%',
                  minHeight: 720,
                  border: 0,
                  bgcolor: 'transparent',
                }}
              />
            </Paper>
          )}

          {tab === 1 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {data.text}
              </Typography>
            </Paper>
          )}

          {tab === 2 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Window: {data.windowStart} → {data.windowEnd}
              </Typography>
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                  fontSize: 12,
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 640,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(
                  {
                    reportDateStr: data.reportDateStr,
                    totals: data.digest.totals,
                    perKind: data.digest.perKind,
                    failureGroups: data.digest.failureGroups,
                    missing: data.digest.missing,
                    livestream: data.livestream,
                  },
                  null,
                  2,
                )}
              </Typography>
            </Paper>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Production send (pipelines Mini):{' '}
            <Link component="span" sx={{ fontFamily: 'monospace' }}>
              yarn pipeline:digest:email --force
            </Link>
          </Typography>
        </>
      )}
    </Container>
  );
};

export default AdminPipelineDigestPreview;
